import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import axios from 'axios';
import { FileText, Video, CheckSquare, AlertCircle, ChevronRight, Eye, Stethoscope, Clock, Users, Target, Shield, BookOpen, Loader2, ExternalLink, ClipboardList, Heart, Check, Calendar, MapPin, Phone, Star, User } from 'lucide-react';
import { SPECIALIZATION_CONTENT } from '../data/specializationContent';
import { AnimatedCounter } from '../components/common/AnimatedCounter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Specialization = () => {
  const { category } = useParams();
  const [consents, setConsents] = useState([]);
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(true);

  useEffect(() => {
    loadContent();
    loadArticles();
    window.scrollTo(0, 0);
  }, [category]);

  const loadContent = async () => {
    try {
      const consentsRes = await axios.get(`${API}/consents`);
      setConsents(consentsRes.data);
    } catch (error) {
      console.error('Error loading content:', error);
    }
  };

  const loadArticles = async () => {
    setArticlesLoading(true);
    try {
      const res = await axios.get(`${API}/specialization/${category}/articles`);
      setArticles(res.data.articles || []);
    } catch (error) {
      console.error('Error loading articles:', error);
      setArticles([]);
    } finally {
      setArticlesLoading(false);
    }
  };

  const content = SPECIALIZATION_CONTENT[category] || SPECIALIZATION_CONTENT.refractive_errors;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16 px-4 relative overflow-hidden" style={{background: 'radial-gradient(circle at 50% 50%, #2d7fb7, #45abe9)'}}>
        <div className="absolute inset-0 opacity-15" style={{backgroundImage: "url('/bg-blue-1.jpg')", backgroundSize: 'cover', backgroundPosition: 'center'}} />
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase mb-4 text-white/70">
                Le Nostre Specializzazioni
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 text-white">{content.title}</h1>
              <p className="text-lg mb-6 text-white/90">{content.subtitle}</p>
              <p className="text-base leading-relaxed text-white/80">{content.intro}</p>
            </div>
            <img 
              src={content.image} 
              alt={content.title} 
              className="rounded-2xl shadow-2xl w-full h-96 object-cover"
            />
          </div>
        </div>
        
        {/* Decorative */}
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full opacity-10" style={{background: 'radial-gradient(circle, #2A9FCB 0%, transparent 70%)'}}></div>
      </section>

      {/* Emotional Content Section (Strabismus) */}
      {content.emotionalContent && (
        <section className="py-20 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            {/* Main emotional title */}
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-normal mb-10 leading-tight tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#2c4667' }}
              data-testid="strabismo-emotional-title"
            >
              {content.emotionalContent.mainTitle}
            </h2>
            
            {/* Opening paragraphs */}
            <div className="space-y-6 mb-16">
              {content.emotionalContent.openingParagraphs.map((p, idx) => (
                <p key={idx} className="text-lg leading-relaxed" style={{ color: '#2c4667' }}>{p}</p>
              ))}
            </div>
            
            {/* Content sections */}
            <div className="space-y-16">
              {content.emotionalContent.sections.map((section, idx) => (
                <div key={idx}>
                  <h3 
                    className="text-2xl sm:text-3xl font-normal mb-6 leading-tight"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#2c4667' }}
                  >
                    {section.title}
                  </h3>
                  <div className="space-y-5">
                    {section.paragraphs.map((p, pIdx) => (
                      <p key={pIdx} className="text-lg leading-relaxed" style={{ color: '#2c4667' }}>{p}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Why Us Section */}
            <div className="mt-20">
              <h3 
                className="text-2xl sm:text-3xl font-normal mb-4 leading-tight"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#2c4667' }}
              >
                {content.emotionalContent.whyUsTitle}
              </h3>
              <p className="text-lg mb-10" style={{ color: '#2c4667' }}>{content.emotionalContent.whyUsIntro}</p>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {content.emotionalContent.whyUsCards.map((card, idx) => (
                  <div 
                    key={idx} 
                    className="bg-white rounded-xl p-6 shadow-md border border-gray-100 hover:shadow-lg transition-shadow"
                    data-testid={`why-us-card-${idx}`}
                  >
                    <h4 className="font-semibold text-lg mb-3" style={{ color: '#2a9fcb' }}>{card.title}</h4>
                    <p className="text-base leading-relaxed" style={{ color: '#2c4667' }}>{card.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Closing message + CTA */}
            <div className="mt-16 p-8 rounded-2xl text-center" style={{ background: '#ffffff', border: '1px solid #e0e7ee' }}>
              <p className="text-lg leading-relaxed mb-8 font-medium" style={{ color: '#2c4667' }}>
                {content.emotionalContent.closingMessage}
              </p>
              <Link 
                to="/prenota"
                className="inline-flex items-center gap-2 px-10 py-4 rounded-xl text-white font-medium transition-all hover:opacity-90 shadow-lg hover:shadow-xl hover:-translate-y-1"
                style={{ background: '#FA7314' }}
                data-testid="strabismo-prenota-cta"
              >
                Prenota la tua prima visita
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Team Section (Strabismus) */}
      {content.teamSection && (
        <section className="py-20 px-4" style={{ background: 'radial-gradient(circle at 50% 50%, #2d7fb7, #45abe9)' }}>
          <div className="max-w-6xl mx-auto">
            <h2 
              className="text-3xl sm:text-4xl font-normal mb-12 text-center tracking-tight text-white"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              data-testid="team-section-title"
            >
              {content.teamSection.title}
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {content.teamSection.members.map((member, idx) => (
                <div 
                  key={idx} 
                  className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-center"
                  data-testid={`team-card-${idx}`}
                >
                  <div className="w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: '#2A9FCB' }}>
                    <User className="w-12 h-12 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-1" style={{ color: '#2c4667' }}>{member.name}</h3>
                  <p className="text-sm font-medium mb-4" style={{ color: '#2a9fcb' }}>{member.role}</p>
                  <p className="text-base leading-relaxed" style={{ color: '#2c4667' }}>{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Numbers Section (Strabismus) */}
      {content.numbersSection && (
        <section className="py-20 px-4 relative overflow-hidden" style={{ background: '#2c4667' }} data-testid="numbers-section">
          <div className="absolute inset-0 opacity-20" style={{backgroundImage: "url('/bg-blue-2.jpg')", backgroundSize: 'cover', backgroundPosition: 'center'}} />
          <div className="relative z-10 max-w-6xl mx-auto">
            <h2 
              className="text-3xl sm:text-4xl font-normal mb-14 text-center tracking-tight text-white"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {content.numbersSection.title}
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {content.numbersSection.stats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                  <p className="text-white/80 mt-3 text-base font-medium">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Locations Section (Strabismus) */}
      {content.locationsSection && (
        <section className="py-20 px-4 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 
              className="text-3xl sm:text-4xl font-normal mb-12 text-center tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#2c4667' }}
              data-testid="locations-section-title"
            >
              {content.locationsSection.title}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {content.locationsSection.locations.map((loc, idx) => (
                <div 
                  key={idx} 
                  className="rounded-2xl overflow-hidden shadow-lg border border-gray-100"
                  data-testid={`location-card-${idx}`}
                >
                  <div className="h-48 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2A9FCB 0%, #2c4667 100%)' }}>
                    <MapPin className="w-16 h-16 text-white/80" />
                  </div>
                  <div className="p-8">
                    <h3 className="text-2xl font-semibold mb-4" style={{ color: '#2c4667' }}>{loc.city}</h3>
                    <div className="space-y-3">
                      <p className="flex items-start gap-3 text-base" style={{ color: '#2c4667' }}>
                        <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#2a9fcb' }} />
                        {loc.address}
                      </p>
                      <p className="flex items-center gap-3 text-base" style={{ color: '#2c4667' }}>
                        <Phone className="w-5 h-5 flex-shrink-0" style={{ color: '#2a9fcb' }} />
                        {loc.phone}
                      </p>
                      <p className="flex items-start gap-3 text-base" style={{ color: '#2c4667' }}>
                        <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#2a9fcb' }} />
                        {loc.hours}
                      </p>
                    </div>
                    <a 
                      href={loc.mapLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-xl text-white font-medium transition-all hover:opacity-90"
                      style={{ background: '#FA7314' }}
                    >
                      <MapPin className="w-4 h-4" />
                      Indicazioni
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section (Strabismus) */}
      {content.testimonialsSection && (
        <section className="py-20 px-4" style={{ background: 'radial-gradient(circle at 50% 50%, #2d7fb7, #45abe9)' }}>
          <div className="max-w-5xl mx-auto">
            <h2 
              className="text-3xl sm:text-4xl font-normal mb-12 text-center tracking-tight text-white"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              data-testid="testimonials-section-title"
            >
              {content.testimonialsSection.title}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {content.testimonialsSection.reviews.map((review, idx) => (
                <div 
                  key={idx} 
                  className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow"
                  data-testid={`testimonial-card-${idx}`}
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: review.stars }).map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-base leading-relaxed mb-6 italic" style={{ color: '#2c4667' }}>
                    "{review.text}"
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold" style={{ color: '#2c4667' }}>{review.name}</span>
                    <span className="text-sm" style={{ color: '#2a9fcb' }}>{review.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA Section (Strabismus) */}
      {content.emotionalContent && (
        <section className="py-20 px-4" style={{ background: '#2c4667' }} data-testid="final-cta-section">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xl md:text-2xl leading-relaxed text-white/90 mb-10">
              {content.emotionalContent.closingMessage}
            </p>
            <Link 
              to="/prenota"
              className="inline-flex items-center gap-3 px-12 py-5 rounded-xl text-white text-lg font-medium transition-all hover:opacity-90 shadow-xl hover:shadow-2xl hover:-translate-y-1"
              style={{ background: '#FA7314' }}
              data-testid="final-prenota-cta"
            >
              Prenota la tua prima visita
              <ChevronRight className="w-6 h-6" />
            </Link>
          </div>
        </section>
      )}

      {/* Visit Includes - Solo per visita pediatrica */}
      {content.visitIncludes && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-10">
              <ClipboardList className="w-8 h-8" style={{color: '#2c4667'}} />
              <h2 className="text-3xl font-bold" style={{color: '#2c4667'}}>{content.visitIncludes.title}</h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {content.visitIncludes.items.map((item, idx) => (
                <div key={idx} className="bg-gradient-to-br from-sky-50 to-white rounded-xl p-6 shadow-sm border border-sky-100 hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}>
                    <Check className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{color: '#2c4667'}}>{item.name}</h3>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How to Prepare - Solo per visita pediatrica */}
      {content.howToPrepare && (
        <section className="py-16 px-4" style={{background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)'}}>
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-10">
              <Heart className="w-8 h-8" style={{color: '#B45309'}} />
              <h2 className="text-3xl font-bold" style={{color: '#B45309'}}>{content.howToPrepare.title}</h2>
            </div>
            
            <div className="space-y-6">
              {content.howToPrepare.tips.map((tip, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 shadow-md flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{background: '#B45309'}}>
                    {idx + 1}
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2" style={{color: '#B45309'}}>{tip.title}</h4>
                    <p className="text-gray-700">{tip.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-10 text-center">
              <Link 
                to="/prenota"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-semibold transition-all hover:opacity-90 shadow-lg"
                style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}
              >
                <Calendar className="w-5 h-5" />
                Prenota la Visita Pediatrica
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Conditions */}
      {content.conditions && content.conditions.length > 0 && (
        <section className="py-16 px-4 bg-[#F8FAFB]">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-10">
              <Eye className="w-8 h-8" style={{color: '#2c4667'}} />
              <h2 className="text-3xl font-bold" style={{color: '#2c4667'}}>Condizioni e Patologie</h2>
            </div>
            
            <div className="space-y-8">
              {content.conditions.map((condition, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-8 shadow-lg" style={{border: '1px solid #E5E7EB'}}>
                  <h3 className="text-2xl font-bold mb-4" style={{color: '#2c4667'}}>{condition.name}</h3>
                  <p className="text-lg mb-6 leading-relaxed" style={{color: '#4B5563'}}>{condition.description}</p>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    {condition.symptoms && condition.symptoms.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3 flex items-center gap-2" style={{color: '#2c4667'}}>
                          <AlertCircle className="w-5 h-5" style={{color: '#2A9FCB'}} />
                          Sintomi
                        </h4>
                        <ul className="space-y-2">
                          {condition.symptoms.map((symptom, i) => (
                            <li key={i} className="flex items-start gap-2 text-gray-600">
                              <ChevronRight className="w-4 h-4 mt-1 flex-shrink-0" style={{color: '#2A9FCB'}} />
                              {symptom}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {condition.causes && condition.causes.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3 flex items-center gap-2" style={{color: '#2c4667'}}>
                          <Target className="w-5 h-5" style={{color: '#FA7314'}} />
                          Cause
                        </h4>
                        <ul className="space-y-2">
                          {condition.causes.map((cause, i) => (
                            <li key={i} className="flex items-start gap-2 text-gray-600">
                              <ChevronRight className="w-4 h-4 mt-1 flex-shrink-0" style={{color: '#FA7314'}} />
                              {cause}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  
                  {condition.treatment && (
                    <div className="mt-6 p-4 rounded-xl" style={{background: 'rgba(127, 179, 160, 0.1)'}}>
                      <h4 className="font-semibold mb-2 flex items-center gap-2" style={{color: '#2c4667'}}>
                        <Stethoscope className="w-5 h-5" style={{color: '#2A9FCB'}} />
                        Trattamento
                      </h4>
                      <p className="text-gray-600">{condition.treatment}</p>
                    </div>
                  )}
                  
                  {condition.prevalence && (
                    <p className="mt-4 text-sm italic" style={{color: '#6B7280'}}>
                      <Users className="w-4 h-4 inline mr-1" />
                      {condition.prevalence}
                    </p>
                  )}
                  
                  {condition.linkTo && condition.linkText && (
                    <Link 
                      to={condition.linkTo}
                      className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90"
                      style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}
                    >
                      {condition.linkText}
                      <ChevronRight className="w-5 h-5" />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Age Info (for strabismus) */}
      {content.ageInfo && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-10">
              <Clock className="w-8 h-8" style={{color: '#2c4667'}} />
              <h2 className="text-3xl font-bold" style={{color: '#2c4667'}}>{content.ageInfo.title}</h2>
            </div>
            
            <div className="space-y-4">
              {content.ageInfo.content.map((item, idx) => (
                <div key={idx} className="flex gap-4 p-6 rounded-xl" style={{background: '#ffffff', border: '1px solid #e0e7ee'}}>
                  <div className="flex-shrink-0 w-24 h-24 rounded-xl flex items-center justify-center" style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}>
                    <span className="text-white font-bold text-center text-sm">{item.age}</span>
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed">{item.info}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Diagnostic Tip (for dry eye) */}
      {content.diagnosticTip && (
        <section className="py-16 px-4" style={{background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)'}}>
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4 p-8 bg-white rounded-2xl shadow-lg border border-amber-200">
              <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center" style={{background: 'linear-gradient(135deg, #F59E0B, #D97706)'}}>
                <Stethoscope className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3" style={{color: '#92400E'}}>{content.diagnosticTip.title}</h3>
                <p className="text-gray-700 leading-relaxed">{content.diagnosticTip.content}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Key Points (for amblyopia) */}
      {content.keyPoints && (
        <section className="py-16 px-4" style={{background: '#FEF3C7'}}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-10 flex items-center gap-3" style={{color: '#B45309'}}>
              <AlertCircle className="w-8 h-8" />
              {content.keyPoints.title}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {content.keyPoints.points.map((point, idx) => (
                <div key={idx} className="bg-white p-6 rounded-xl shadow-lg" style={{border: '2px solid #F59E0B'}}>
                  <h3 className="font-bold text-lg mb-3" style={{color: '#B45309'}}>{point.title}</h3>
                  <p className="text-gray-700">{point.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Complications (for strabismus) */}
      {content.complications && (
        <section className="py-16 px-4" style={{background: 'radial-gradient(circle at 50% 50%, #2d7fb7, #45abe9)'}}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-10 flex items-center gap-3" style={{color: '#2c4667'}}>
              <AlertCircle className="w-8 h-8" />
              Possibili Complicanze
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {content.complications.map((comp, idx) => (
                <div key={idx} className="bg-white p-6 rounded-xl shadow-lg" style={{border: '1px solid #E5E7EB'}}>
                  <h3 className="font-bold mb-3" style={{color: '#EF4444'}}>{comp.name}</h3>
                  <p className="text-sm text-gray-500">{comp.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Eligibility (for refractive surgery) */}
      {content.eligibility && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-10">
              <Shield className="w-8 h-8" style={{color: '#2A9FCB'}} />
              <h2 className="text-3xl font-bold" style={{color: '#2c4667'}}>{content.eligibility.title}</h2>
            </div>
            
            <div className="space-y-4">
              {content.eligibility.requirements.map((req, idx) => (
                <div key={idx} className="p-6 rounded-xl" style={{background: '#ffffff', border: '1px solid #e0e7ee'}}>
                  <h4 className="font-bold mb-2" style={{color: '#2c4667'}}>{req.criterion}</h4>
                  <p className="text-gray-600">{req.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* IOL Types (for cataract) */}
      {content.iolTypes && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-10" style={{color: '#2c4667'}}>{content.iolTypes.title}</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {content.iolTypes.types.map((iol, idx) => (
                <div key={idx} className="p-6 rounded-xl shadow-lg bg-white" style={{border: '1px solid #E5E7EB'}}>
                  <h4 className="font-bold text-lg mb-3" style={{color: '#2c4667'}}>{iol.name}</h4>
                  <p className="text-gray-500 mb-4">{iol.description}</p>
                  <p className="text-sm px-3 py-2 rounded-lg" style={{background: 'rgba(127, 179, 160, 0.1)', color: '#2A9FCB'}}>
                    <strong>Ideale per:</strong> {iol.ideal}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Surgery Steps (for cataract) */}
      {content.surgery && (
        <section className="py-16 px-4" style={{background: '#ffffff', border: '1px solid #e0e7ee'}}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-10" style={{color: '#2c4667'}}>{content.surgery.title}</h2>
            
            <div className="space-y-4 mb-8">
              {content.surgery.steps.map((step, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4 bg-white rounded-xl">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}>
                    <span className="text-white font-bold text-sm">{idx + 1}</span>
                  </div>
                  <p className="text-gray-600">{step}</p>
                </div>
              ))}
            </div>
            
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="p-6 bg-white rounded-xl">
                <h4 className="font-bold mb-2" style={{color: '#2c4667'}}>Durata</h4>
                <p className="text-gray-600">{content.surgery.duration}</p>
              </div>
              <div className="p-6 bg-white rounded-xl">
                <h4 className="font-bold mb-2" style={{color: '#2c4667'}}>Recupero</h4>
                <p className="text-gray-600">{content.surgery.recovery}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Treatments (generic) */}
      {content.treatments && (
        <section className="py-16 px-4" style={{background: 'radial-gradient(circle at 50% 50%, #2d7fb7, #45abe9)'}}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-10" style={{color: '#2c4667'}}>{content.treatments.title}</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {content.treatments.options.map((option, idx) => (
                <div key={idx} className="p-6 bg-white rounded-xl shadow-lg" style={{border: '1px solid #E5E7EB'}}>
                  <h4 className="font-bold mb-3" style={{color: '#2c4667'}}>{option.name}</h4>
                  <p className="text-gray-500 mb-2">{option.desc}</p>
                  {option.ideal && (
                    <p className="text-sm mt-3 px-3 py-2 rounded-lg" style={{background: 'rgba(127, 179, 160, 0.1)', color: '#2A9FCB'}}>
                      <strong>Ideale per:</strong> {option.ideal}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Stages (for keratoconus) */}
      {content.stages && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-10" style={{color: '#2c4667'}}>{content.stages.title}</h2>
            
            <div className="flex flex-col md:flex-row gap-4">
              {content.stages.levels.map((level, idx) => (
                <div key={idx} className="flex-1 p-6 rounded-xl text-center" style={{
                  background: `rgba(127, 179, 160, ${0.1 + idx * 0.15})`
                }}>
                  <h4 className="font-bold text-lg mb-2" style={{color: '#2c4667'}}>{level.stage}</h4>
                  <p className="text-sm text-gray-500">{level.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Self Test (for maculopathy) */}
      {content.selfTest && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="p-8 rounded-2xl" style={{background: 'linear-gradient(135deg, rgba(127, 179, 160, 0.2) 0%, rgba(90, 155, 130, 0.1) 100%)'}}>
              <h2 className="text-2xl font-bold mb-6" style={{color: '#2c4667'}}>{content.selfTest.title}</h2>
              
              <ol className="space-y-3">
                {content.selfTest.instructions.map((instruction, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold" style={{background: '#2A9FCB'}}>
                      {idx + 1}
                    </span>
                    <span className="text-gray-600">{instruction}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>
      )}

      {/* Diagnosis (for glaucoma) */}
      {content.diagnosis && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-10" style={{color: '#2c4667'}}>{content.diagnosis.title}</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {content.diagnosis.tests.map((test, idx) => (
                <div key={idx} className="p-5 rounded-xl" style={{background: '#ffffff', border: '1px solid #e0e7ee'}}>
                  <h4 className="font-bold mb-2" style={{color: '#2c4667'}}>{test.name}</h4>
                  <p className="text-sm text-gray-500">{test.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Risk Factors */}
      {content.riskFactors && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-10" style={{color: '#2c4667'}}>Fattori di Rischio</h2>
            
            <div className="flex flex-wrap gap-3">
              {content.riskFactors.map((factor, idx) => (
                <span key={idx} className="px-4 py-2 rounded-full text-sm font-medium" style={{background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA'}}>
                  {factor}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Results (for refractive surgery) */}
      {content.results && (
        <section className="py-16 px-4" style={{background: '#ffffff', border: '1px solid #e0e7ee'}}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-10" style={{color: '#2c4667'}}>{content.results.title}</h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {content.results.content.map((result, idx) => (
                <div key={idx} className="p-5 bg-white rounded-xl flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{background: '#22C55E'}}>
                    <CheckSquare className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-gray-600">{result}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Risks (for refractive surgery) */}
      {content.risks && content.risks.length > 0 && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-10" style={{color: '#2c4667'}}>Rischi e Complicanze</h2>
            
            <div className="space-y-3">
              {content.risks.map((risk, idx) => (
                <div key={idx} className="p-4 rounded-xl flex items-start gap-3" style={{background: 'rgba(239, 68, 68, 0.05)'}}>
                  <AlertCircle className="w-5 h-5 flex-shrink-0 text-amber-500" />
                  <p className="text-gray-600">{risk}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Prevention */}
      {content.prevention && content.prevention.length > 0 && (
        <section className="py-16 px-4" style={{background: '#ffffff', border: '1px solid #e0e7ee'}}>
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-10">
              <Shield className="w-8 h-8" style={{color: '#2A9FCB'}} />
              <h2 className="text-3xl font-bold" style={{color: '#2c4667'}}>Prevenzione</h2>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {content.prevention.map((item, idx) => (
                <div key={idx} className="p-5 bg-white rounded-xl flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{background: '#2A9FCB'}}>
                    <ChevronRight className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-gray-600">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* When to See Doctor */}
      {content.whenToSeeDoctor && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="p-8 rounded-2xl" style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}>
              <h2 className="text-2xl font-bold text-white mb-4">Quando Consultare l'Oculista</h2>
              <p className="text-white/90 text-lg leading-relaxed">{content.whenToSeeDoctor}</p>
              <Link 
                to="/booking" 
                className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-white rounded-full  font-semibold transition-all hover:scale-105"
                style={{color: '#2A9FCB'}}
              >
                Prenota una Visita
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section - SEO optimized with schema.org FAQPage */}
      {content.faq && content.faq.length > 0 && (
        <section className="py-16 px-4 bg-white" data-testid="faq-section">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-10">
              <BookOpen className="w-8 h-8" style={{color: content.themeColor || '#2A9FCB'}} />
              <h2 className="text-3xl font-bold" style={{color: '#2c4667'}}>Domande Frequenti</h2>
            </div>
            
            <div 
              itemScope 
              itemType="https://schema.org/FAQPage"
              className="space-y-4"
            >
              {content.faq.map((item, idx) => (
                <details 
                  key={idx} 
                  className="group rounded-xl border border-gray-200 overflow-hidden hover:border-sky-300 transition-colors"
                  itemScope 
                  itemProp="mainEntity" 
                  itemType="https://schema.org/Question"
                  data-testid={`faq-item-${idx}`}
                >
                  <summary 
                    className="flex items-center justify-between px-6 py-5 cursor-pointer select-none bg-gray-50 group-open:bg-sky-50 transition-colors"
                    style={{listStyle: 'none'}}
                  >
                    <h3 itemProp="name" className="text-base font-semibold pr-4" style={{color: '#1e3a5f'}}>
                      {item.q}
                    </h3>
                    <ChevronRight className="w-5 h-5 flex-shrink-0 text-gray-400 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div 
                    itemScope 
                    itemProp="acceptedAnswer" 
                    itemType="https://schema.org/Answer"
                    className="px-6 pb-6 pt-2"
                  >
                    <div itemProp="text" className="text-gray-600 leading-relaxed text-sm" dangerouslySetInnerHTML={{__html: item.a}} />
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Scientific Articles */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-10">
            <BookOpen className="w-8 h-8" style={{color: '#2A9FCB'}} />
            <h2 className="text-3xl font-bold" style={{color: '#2c4667'}}>Articoli Scientifici Recenti</h2>
          </div>
          
          {articlesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin" style={{color: '#2A9FCB'}} />
              <span className="ml-3 text-gray-400">Caricamento articoli...</span>
            </div>
          ) : articles.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              {articles.map((article, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow" style={{border: '1px solid #E5E7EB'}}>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}>
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="text-xs font-medium px-2 py-1 rounded-full " style={{background: 'rgba(127, 179, 160, 0.15)', color: '#2A9FCB'}}>
                        {article.journal} • {article.year}
                      </span>
                    </div>
                  </div>
                  
                  {article.link ? (
                    <a 
                      href={article.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-bold text-lg mb-2 block hover:underline cursor-pointer"
                      style={{color: '#2A9FCB'}}
                      data-testid={`article-link-${idx}`}
                    >
                      {article.title}
                      <ExternalLink className="w-4 h-4 inline ml-2" />
                    </a>
                  ) : (
                    <h3 className="font-bold text-lg mb-2" style={{color: '#2c4667'}}>{article.title}</h3>
                  )}
                  <p className="text-sm text-gray-500 mb-3">{article.authors}</p>
                  <p className="text-sm text-gray-600 leading-relaxed">{article.summary}</p>
                  
                  <div className="mt-4 pt-4" style={{borderTop: '1px solid #E5E7EB'}}>
                    {article.link ? (
                      <a 
                        href={article.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs flex items-center gap-1 hover:underline"
                        style={{color: '#2A9FCB'}}
                      >
                        <ExternalLink className="w-3 h-3" />
                        {article.link.includes('pubmed') ? 'Fonte: PubMed' : article.link.includes('sedesoi') ? 'Fonte: SISO' : 'Fonte: letteratura scientifica'}
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" />
                        Fonte: letteratura scientifica
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              Nessun articolo disponibile al momento
            </div>
          )}
          
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-400">
              Gli articoli sono generati da AI basandosi sulla letteratura scientifica recente e hanno scopo puramente informativo.
            </p>
          </div>
        </div>
      </section>

      {/* Consents */}
      <section className="py-16 px-4" style={{background: 'radial-gradient(circle at 50% 50%, #2d7fb7, #45abe9)'}}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center mb-6">
            <CheckSquare className="w-6 h-6 mr-3" style={{color: '#2A9FCB'}} />
            <h2 className="text-3xl font-bold" style={{color: '#2c4667'}}>Consensi Informati</h2>
          </div>
          <p className="mb-8" style={{color: '#4B5563'}}>Prima di qualsiasi procedura, forniamo consensi informati dettagliati per garantire la massima trasparenza.</p>
          <div className="bg-white rounded-lg p-6 border" style={{borderColor: '#E5E7EB'}}>
            <p className="text-sm" style={{color: '#6B7280'}}>I consensi informati specifici per questa procedura saranno forniti durante la consulenza medica.</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Specialization;
